import React from 'react';
import { Icon, Spinner, Button } from '@blueprintjs/core';
import moment from 'moment';
import { normalizeHex, serviceTypeColors } from 'helpers/serviceTypeColor';

// Presentational pieces shared by the booking edit page (pages/bookings/show.js)
// and the new booking page (pages/bookings/new.js). They hold no store logic:
// each page reads its own fields in render and passes plain values down, so the
// observer pages keep tracking everything. Styles: styles/booking-form.scss
// (scoped under .bk-page) and styles/booking-ticket.scss.

const STATUS_PILLS = {
  confirmed: { label: 'Confirmed', tone: 'green' },
  draft: { label: 'Draft', tone: 'grey' },
};

export const BookingStatusPill = ({ status }) => {
  const key = (status || 'draft').toString().toLowerCase();
  const pill = STATUS_PILLS[key] || { label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '), tone: 'grey' };
  return <span className={`bk-pill bk-pill--${pill.tone}`}>{pill.label}</span>;
};

// `showStatus={false}` for a booking that isn't saved yet.
export const BookingPageHeader = ({ title, status, showStatus = true, meta, children }) => (
  <header className="bk-header">
    <div className="bk-header__heading">
      <div className="bk-header__title-row">
        <h1 className="bk-header__title">{title}</h1>
        {showStatus && <BookingStatusPill status={status} />}
      </div>
      {meta && <p className="bk-header__meta">{meta}</p>}
    </div>
    <div className="bk-header__actions">{children}</div>
  </header>
);

export const BookingSection = ({ number, title, helper, children, className }) => (
  <section className={className ? `bk-section ${className}` : 'bk-section'}>
    <div className="bk-section__rail" aria-hidden="true">
      <span className="bk-section__marker">{number}</span>
    </div>
    <div className="bk-section__body">
      <div className="bk-section__head">
        <h2 className="bk-section__title">{title}</h2>
        {helper && <p className="bk-section__helper">{helper}</p>}
      </div>
      {children}
    </div>
  </section>
);

// Colour dot shown inside a <select> wrapper for the chosen service type.
export const ServiceTypeSelectWrap = ({ color, children }) => {
  const hex = normalizeHex(color);
  return (
    <div className={`bk-select${hex ? ' bk-select--dot' : ''}`}>
      {hex && <span className="bk-select__dot" style={{ backgroundColor: hex }} aria-hidden="true" />}
      {children}
    </div>
  );
};

// Optional: title (drop zone text), disabled (no uploading or removing, e.g. a
// confirmed booking on the public page).
export const BookingAttachments = ({ attachments, uploading, onChange, onRemove, title, disabled }) => (
  <div className={`bk-upload${disabled ? ' bk-upload--disabled' : ''}`}>
    <label htmlFor="file-upload" className="bk-upload__drop">
      <span className="bk-upload__icon"><Icon icon="paperclip" iconSize={18} /></span>
      <span className="bk-upload__text">
        <span className="bk-upload__title">{title || 'Attach files'}</span>
        <span className="bk-upload__hint">PNG, JPG, GIF, PDF, DOC, DOCX, XLS, XLSX</span>
      </span>
      {uploading && <Spinner size={Spinner.SIZE_SMALL} />}
    </label>
    <input id="file-upload" className="bk-upload__input" type="file" multiple={true} disabled={disabled ? true : undefined} onChange={onChange} />
    {attachments && attachments.length > 0 &&
      <ul className="bk-files">
        {attachments.map((attachment) => (
          <li className="bk-file" key={attachment.id || attachment.asset.url}>
            <span className="bk-file__icon"><Icon icon="document" iconSize={16} /></span>
            <div className="bk-file__text">
              <a className="bk-file__name" href={attachment.asset.url} target="_blank" rel="noopener noreferrer" download>
                {attachment.asset.url.split("/").pop()}
              </a>
              <span className="bk-file__meta">
                {attachment.asset_content_type}
                {attachment.asset_human_file_size ? `, ${attachment.asset_human_file_size}` : ''}
              </span>
            </div>
            <Button
              className="bk-file__remove"
              minimal={true}
              small={true}
              icon="trash"
              aria-label="Remove file"
              disabled={disabled ? true : undefined}
              onClick={() => onRemove(attachment)}
            />
          </li>
        ))}
      </ul>
    }
  </div>
);

// The booking ticket (markup documented in styles/booking-ticket.scss).
// groups: [{ key, title, lines: [{ key, label, amount }] }]
export const BookingTicket = ({
  date, customerName, address, contact, serviceName, serviceDetail, serviceAmount, serviceColor,
  groups, totalLabel, total, children, showTotal = true,
}) => {
  const when = date ? moment(date) : null;
  const validWhen = when && when.isValid() ? when : null;
  const hex = normalizeHex(serviceColor);
  const visibleGroups = (groups || []).filter((group) => group.lines && group.lines.length > 0);
  return (
    <aside className="bk-ticket">
      <div className="bk-ticket__when">
        <span className="bk-ticket__date">{validWhen ? validWhen.format('ddd, MMM D, YYYY') : 'No date yet'}</span>
        {validWhen && <span className="bk-ticket__time">{validWhen.format('h:mm A')}</span>}
      </div>
      <div className="bk-ticket__body">
        <div className="bk-ticket__row">
          <span className="bk-ticket__icon"><Icon icon="person" iconSize={14} /></span>
          <div className="bk-ticket__text">
            <span className="bk-ticket__label">Customer</span>
            <span className="bk-ticket__value">{customerName || 'Not entered yet'}</span>
            {contact && <span className="bk-ticket__muted">{contact}</span>}
          </div>
        </div>
        {address &&
          <div className="bk-ticket__row">
            <span className="bk-ticket__icon"><Icon icon="map-marker" iconSize={14} /></span>
            <div className="bk-ticket__text">
              <span className="bk-ticket__label">Address</span>
              <span className="bk-ticket__muted">{address}</span>
            </div>
          </div>
        }
        {serviceName ?
          <div className="bk-ticket__service" style={hex ? { '--bk-service': hex } : undefined}>
            <span className="bk-ticket__service-name">{serviceName}</span>
            {serviceDetail && <span className="bk-ticket__muted">{serviceDetail}</span>}
            <span className="bk-ticket__amount">{serviceAmount}</span>
          </div>
          :
          <span className="bk-ticket__empty">No service selected yet</span>
        }
        {visibleGroups.map((group) => (
          <div className="bk-ticket__group" key={group.key}>
            <span className="bk-ticket__label">{group.title}</span>
            <ul className="bk-ticket__lines">
              {group.lines.map((line) => (
                <li key={line.key}><span>{line.label}</span><span className="bk-ticket__amount">{line.amount}</span></li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="bk-ticket__tear" aria-hidden="true"></div>
      <div className="bk-ticket__foot">
        {showTotal && <div className="bk-ticket__total"><span>{totalLabel || 'Total (estimate)'}</span><strong>{total}</strong></div>}
        {children}
      </div>
    </aside>
  );
};

// Tenant booking colour (booking_form_color, "RRGGBB" with or without "#") as
// CSS custom properties for a .bk-page root, for the public booking pages.
// --bk-ring is a full box-shadow there (see booking-form.scss); --bk-ring-color
// is the bare colour. An optional background (booking_form_bgcolor) becomes
// --bk-public-bg with a readable --bk-on-page text colour.
export const bookingBrandStyle = (color, background) => {
  const hex = normalizeHex(color) || '#1800FF';
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.substr(i, 2), 16));
  const shade = (value) => Math.round(value * 0.85);
  const style = {
    '--bk-brand': hex,
    '--bk-brand-hover': `rgb(${shade(r)}, ${shade(g)}, ${shade(b)})`,
    '--bk-brand-tint': `rgba(${r}, ${g}, ${b}, 0.1)`,
    '--bk-ring': `0 0 0 3px rgba(${r}, ${g}, ${b}, 0.18)`,
    '--bk-ring-color': `rgba(${r}, ${g}, ${b}, 0.18)`,
    '--bk-brand-shadow': `rgba(${r}, ${g}, ${b}, 0.24)`,
    '--bk-on-brand': serviceTypeColors(hex).text,
  };
  const bg = normalizeHex(background);
  if (bg) {
    style['--bk-public-bg'] = bg;
    style['--bk-on-page'] = serviceTypeColors(bg).text;
  }
  return style;
};

// Joins address parts with commas, skipping blanks.
// One line for the ticket. Address autocomplete fills `address` with a full
// address ("10 Main St, Brampton, ON, Canada"), so city and province aren't
// repeated after one; a typed street address gets them added.
// Questions only render inside their question group, so a section of them is
// empty unless at least one question belongs to a loaded group.
export const hasGroupedQuestions = (groups, questions) => Boolean(
  groups && groups.objects && questions && questions.length > 0
  && questions.some((question) => groups.objects.some((group) => group.id === question.question_group_id))
);

export const joinAddress = (address, suite, city, state, zip) => {
  const text = String(address || '');
  const contains = (part) => text.toLowerCase().includes(String(part).toLowerCase());
  const isFull = text.split(',').length >= 3;
  const parts = [address, suite];
  if (!isFull) parts.push(city, state);
  else if (city && !contains(city)) parts.push(city);
  if (zip && !contains(zip)) parts.push(zip);
  return parts.filter((part) => part && String(part).trim() !== '').join(', ');
};

// Loading skeleton for the booking pages. It reuses the real layout classes
// (bk-layout, bk-form, bk-aside), so the page keeps its shape when the
// content replaces it. Styles: booking-form.scss (.bk-skeleton, .bk-bone).
const Bone = ({ width, height = 12, round, className = '' }) => (
  <span className={`bk-bone${round ? ' bk-bone--round' : ''} ${className}`} style={{ width, height }} />
);

const SkeletonSection = ({ number, fields }) => (
  <section className="bk-section bk-skeleton__section">
    <Bone width={26} height={26} round={true} className="bk-skeleton__marker" />
    <div className="bk-skeleton__section-body">
      <Bone width={number === 1 ? 140 : 170} height={18} />
      <Bone width="46%" height={12} />
      <div className="bk-skeleton__fields">
        {fields.map((width, index) => (
          <div key={index} className="bk-skeleton__field" style={{ gridColumn: width === 'full' ? '1 / -1' : undefined }}>
            <Bone width={72 + ((index * 23) % 40)} height={12} />
            <Bone width="100%" height={40} />
          </div>
        ))}
      </div>
    </div>
  </section>
);

// `bar`: reserve the company bar (public pages), `branded` for its taller
// branded form.
export const BookingPageSkeleton = ({ bar = false, branded = false }) => (
  <div className="bk-skeleton" role="status" aria-live="polite" aria-busy="true">
    <span className="bk-skeleton__label">Loading your booking</span>
    {(bar || branded) && (
      <div className={`bk-public__bar${branded ? '' : ' bk-public__bar--plain'}`}>
        <div className="bk-public__bar-inner">
          <Bone width={150} height={28} />
          {branded && <Bone width={220} height={14} />}
        </div>
      </div>
    )}
    <div className="bk-header bk-skeleton__header">
      <Bone width={190} height={28} />
      <Bone width={260} height={14} />
    </div>
    <div className="bk-layout">
      <div className="bk-form">
        <SkeletonSection number={1} fields={[1, 2, 3, 4]} />
        <SkeletonSection number={2} fields={['full', 1, 2, 3]} />
        <SkeletonSection number={3} fields={[1, 2]} />
      </div>
      <div className="bk-aside">
        <div className="bk-skeleton__ticket">
          <div className="bk-skeleton__ticket-when">
            <Bone width="62%" height={24} />
            <Bone width="34%" height={14} />
          </div>
          <div className="bk-skeleton__ticket-body">
            {[0, 1, 2].map((row) => (
              <div key={row} className="bk-skeleton__ticket-row">
                <Bone width={30} height={30} />
                <div className="bk-skeleton__ticket-lines">
                  <Bone width="40%" height={10} />
                  <Bone width={row === 2 ? '55%' : '80%'} height={13} />
                </div>
              </div>
            ))}
            <Bone width="100%" height={44} />
          </div>
        </div>
      </div>
    </div>
  </div>
);
